<template>
  <PageTemplate title="Subscription">
    <div class="space-y-8">
      <Card title="Manage my subscription" v-if="subscription">
        <div>
          <a
            :href="customerPortalUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-white bg-primary hover:bg-primary/70 font-medium rounded-lg p-2 text-sm"
          >
            Manage subscription
          </a>
          <!-- {{ subscription }} -->
          <ul
            class="mt-4 space-y-2 text-sm leading-6 text-gray-600 dark:text-gray-300 capitalize"
          >
            <li>
              <span class="font-semibold">Status:</span>
              {{ statusToText[subscription.status] }}
            </li>
            <li v-if="subscriptionIsActive && subscription.tier_name">
              <span class="font-semibold">Current plan:</span>
              {{ subscription.tier_name?.replace("tier-", "") }}
            </li>
            <li v-if="subscriptionIsActive && subscription.frequency">
              <span class="font-semibold">Current billing frequency:</span>
              {{ subscription.frequency }}
            </li>
            <li v-if="subscriptionIsActive && subscription.current_period_end">
              <span class="font-semibold">Next billing date:</span>
              {{
                new Date(
                  subscription.current_period_end * 1000
                ).toLocaleDateString()
              }}
            </li>
          </ul>
        </div>
      </Card>
      <!-- <div>Select a subscription plan</div> -->
      <!-- <Card title="Plans"> -->
      <div>
        <div>
          <h3
            class="text-lg font-semibold leading-8 text-gray-900 dark:text-white"
          >
            Subscription plans
          </h3>
        </div>
        <div class="flex justify-center">
          <RadioGroup
            v-model="frequency"
            class="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-200"
          >
            <RadioGroupLabel class="sr-only">Payment frequency</RadioGroupLabel>
            <RadioGroupOption
              as="template"
              v-for="option in frequencies"
              :key="option.value"
              :value="option"
              v-slot="{ checked }"
            >
              <div
                :class="[
                  checked ? 'bg-primary text-white' : 'text-gray-500',
                  'cursor-pointer rounded-full px-2.5 py-1',
                ]"
              >
                <span>{{ option.label }}</span>
              </div>
            </RadioGroupOption>
          </RadioGroup>
        </div>
        <div
          class="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3"
        >
          <div
            v-for="tier in tiers"
            :key="tier.id"
            :class="[
              tier.mostPopular ? 'ring-1 ring-primary' : 'ring-1 ring-gray-200',
              'rounded-3xl p-8 xl:p-10 shadow-md',
            ]"
          >
            <div class="flex items-center justify-between gap-x-4">
              <h3
                :id="tier.id"
                :class="[
                  tier.mostPopular
                    ? 'text-primary'
                    : 'text-gray-900 dark:text-white',
                  'text-lg font-semibold leading-8',
                ]"
              >
                {{ tier.name }}
              </h3>
              <p
                v-if="tier.mostPopular"
                class="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold leading-5 text-primary"
              >
                Most popular
              </p>
            </div>
            <p class="mt-4 text-sm leading-6 text-gray-600">
              {{ tier.description }}
            </p>
            <p
              v-if="tier.customPrice"
              class="mt-6 text-lg font-bold text-gray-900 dark:text-gray-100"
            >
              Custom
            </p>
            <p v-else class="mt-6 flex items-baseline gap-x-1">
              <span
                class="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100"
              >
                {{ tier.price ? tier.price[frequency.value] : "" }}
              </span>
              <span
                v-if="frequency?.priceSuffix"
                class="text-sm font-semibold leading-6 text-gray-600"
                >{{ frequency.priceSuffix }}</span
              >
            </p>
            <!-- selected frequency -->
            <div v-if="frequency.value === 'annually'" class="h-5">
              <p
                v-if="tier.price"
                class="mt-1 text-sm font-semibold leading-6 text-green-600"
              >
                Save 20% with annual billing
              </p>
            </div>

            <button
              v-if="!subscriptionIsActive || tier.id == 'tier-enterprise'"
              :aria-describedby="tier.id"
              :class="[
                tier.mostPopular
                  ? 'bg-primary text-white shadow-sm hover:bg-primary/90'
                  : 'text-primary ring-1 ring-inset ring-primary hover:bg-primary/10',
                'mt-6 w-full block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
              ]"
              @click="subscribe(tier)"
            >
              <div v-if="tier.customPrice">Request Custom Plan</div>
              <div v-else>Subscribe</div>
            </button>
            <ul
              role="list"
              class="mt-8 space-y-3 text-sm leading-6 text-gray-600 dark:text-gray-300 xl:mt-10"
            >
              <li
                v-for="(feature, num) in tier.features"
                :key="feature"
                class="flex gap-x-3"
              >
                <CheckIcon
                  class="h-6 w-5 flex-none text-primary"
                  aria-hidden="true"
                />
                {{ feature }}
              </li>
            </ul>
          </div>
        </div>
        <!-- </Card> -->
      </div>

      <div class="px-4 py-8 flex justify-center w-full">
        <div
          class="border rounded-xl bg-gray-50 dark:bg-slate-700 dark:text-white p-2 text-sm flex flex-row space-x-2"
        >
          <div>Fast requests: <strong>1 credit</strong></div>
          <div>/</div>
          <div>Deep requests: <strong>5 credits</strong></div>
        </div>
      </div>
    </div>
  </PageTemplate>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import PageTemplate from "@/pages/PageTemplate.vue";
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import { CheckIcon } from "@heroicons/vue/20/solid";
import Card from "./Card.vue";
import { DefaultService as api } from "@/client";
import { useClerk, useUser } from "@clerk/vue";

const { user } = useUser();
// active org
const clerk = useClerk();

const org = clerk.value.organization;
const subscription =
  org?.publicMetadata?.subscription || user.value?.publicMetadata?.subscription;

const customerEmail = user.value?.primaryEmailAddress?.emailAddress;

const customerPortalUrl =
  "https://billing.stripe.com/p/login/cN28zd5Af8tDb7y9AA?prefilled_email=" +
  encodeURIComponent(customerEmail);

const frequencies = [
  { value: "monthly", label: "Monthly", priceSuffix: "/month" },
  { value: "annually", label: "Annually", priceSuffix: "/month" },
];
const tiers = [
  {
    name: "Starter",
    id: "tier-starter",
    href: "https://app.ivycheck.com/signup",
    // href: "https://ivycheck.com/request-demo",
    price: { monthly: "$99", annually: "$79", annuallyTotal: "$948" },
    // price: { monthly: "$59", annually: "$49", annuallyTotal: "$598" },
    // free: true,
    // description: "Begin securing your AI app during development.",
    features: [
      "5k credits per month included",
      "5 requests per minute",
      "Fast and deep research requests",
      // "2 users",
      // "Logging",
      "Email support",
    ],
    mostPopular: false,
  },
  {
    name: "Value",
    id: "tier-value",
    href: "https://app.ivycheck.com/signup",
    // href: "https://ivycheck.com/request-demo",
    price: { monthly: "$499", annually: "$399", annuallyTotal: "$4788" },
    // description: "Add protection to your team's AI app in production.",
    features: [
      "50k credits per month included",
      "15 requests per minute",
      "Fast and deep research requests",
      // "Unlimited users",
      // "Logging",
      "Priority support",
    ],
    mostPopular: true,
  },
  {
    name: "Enterprise",
    id: "tier-enterprise",
    href: "https://ivycheck.com/request-demo",
    // price: { monthly: "$60", annually: "$576" },
    customPrice: true,
    // description: "Customized protection and coverage for the enterprise.",
    features: [
      "Custom requests per month",
      "Custom requests per minute",
      "Fast and deep research requests",
      // "Unlimited users",
      // "Logging",
      "Priority support",
      // "Single sign-on",
    ],
    mostPopular: false,
  },
];

const frequency = ref(frequencies[0]);

function subscribe(tier) {
  if (tier.id === "tier-enterprise") {
    // open in new tab
    window.open(tier.href, "_blank");
    return;
  } else
    api
      .createSubscription({
        plan_id: tier.id,
        frequency: frequency.value.value,
        email: customerEmail,
      })
      .then((res) => {
        // console.log(res);
        window.location.href = res.url;
      });
}

// function getSubscription() {
//   api.viewSubscription().then((res) => {
//     console.log(res);
//   });
// }

// getSubscription();

const statusToText = {
  active: "Active",
  canceled: "Canceled",
  incomplete: "Incomplete",
  incomplete_expired: "Incomplete Expired",
  past_due: "Past Due",
  trialing: "Trial active",
};
const subscriptionIsActive = computed(() =>
  ["active", "trialing"].includes(subscription?.status)
);
</script>
